import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Plus, Trash2, Edit2, Save, X, Package, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const blankRow = () => ({
  insured: 'client',
  carrier: '',
  product_name: '',
  product_type: 'term',
  face_amount: 0,
  monthly_benefit: 0,
  premium: 0,
  payment_frequency: 'monthly',
  term_or_duration: '',
  policy_number: '',
  application_date: new Date().toISOString().slice(0, 10),
  issue_date: '',
  riders: '',
  beneficiary: '',
  notes: '',
  rationale: '',
  status: 'submitted',
});

export const CoverageTakenForm = ({ caseId, agent, client, onUpdate }) => {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankRow());
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/compliance/cases/${caseId}/coverage-taken`);
      if (r.ok) setItems(await r.json());
    } catch (e) { console.error(e); }
  }, [caseId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const save = async () => {
    setLoading(true);
    try {
      const body = {
        ...form,
        riders: typeof form.riders === 'string'
          ? form.riders.split(',').map(s => s.trim()).filter(Boolean)
          : form.riders,
      };
      const url = editingId
        ? `${API_URL}/api/compliance/cases/${caseId}/coverage-taken/${editingId}?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`
        : `${API_URL}/api/compliance/cases/${caseId}/coverage-taken?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`;
      const res = await fetch(url, {
        method: editingId ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      setShowAdd(false);
      setEditingId(null);
      setForm(blankRow());
      await fetchItems();
      if (onUpdate) onUpdate();
    } catch (e) {
      alert(e.message);
    } finally { setLoading(false); }
  };

  // Iter 64 — AI-expand the advisor's short rationale into a compliance paragraph.
  // Requires the coverage row to already be saved (needs an item id).
  const aiExpand = async () => {
    if (!editingId) { alert('Save this coverage first, then use AI to expand the rationale.'); return; }
    setAiLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/compliance/cases/${caseId}/coverage-taken/${editingId}/ai-rationale`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ draft: form.rationale || '' }) }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'AI generation failed');
      setForm(prev => ({ ...prev, rationale: data.rationale || prev.rationale }));
    } catch (e) {
      alert(e.message);
    } finally { setAiLoading(false); }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      ...blankRow(),
      ...it,
      riders: Array.isArray(it.riders) ? it.riders.join(', ') : (it.riders || ''),
    });
    setShowAdd(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this coverage record?')) return;
    await fetch(`${API_URL}/api/compliance/cases/${caseId}/coverage-taken/${id}?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`,
      { method: 'DELETE' });
    fetchItems();
  };

  const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString()}`;

  return (
    <Card data-testid="coverage-taken-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5 text-[#003D5C]" /> Coverage Taken
        </CardTitle>
        <Button
          onClick={() => { setShowAdd(!showAdd); setEditingId(null); setForm(blankRow()); }}
          size="sm"
          className="bg-[#0077B6] hover:bg-[#005f8f] text-white"
          data-testid="add-coverage-taken-btn"
        >
          <Plus className="h-4 w-4 mr-1" />{showAdd ? 'Cancel' : 'Add Coverage'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          Record the carrier, product, coverage amount, and premium of the policy the client actually
          chose at the point of sale. Sits between Recommendation and Advisor Notes in the workflow.
        </p>

        {showAdd && (
          <div className="border rounded-lg p-4 bg-blue-50 space-y-3" data-testid="coverage-taken-form">
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Insured</label>
                <select value={form.insured} onChange={(e) => setForm({ ...form, insured: e.target.value })} className="w-full px-2 py-2 border rounded" data-testid="ct-insured">
                  <option value="client">{client?.full_name || 'Client'}</option>
                  {client?.spouse_name && <option value="spouse">{client.spouse_name}</option>}
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Carrier *</label>
                <input value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className="w-full px-2 py-2 border rounded" placeholder="e.g. Manulife" data-testid="ct-carrier" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Product Name</label>
                <input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="w-full px-2 py-2 border rounded" placeholder="e.g. Family Term 20" data-testid="ct-product-name" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Product Type</label>
                <select value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} className="w-full px-2 py-2 border rounded">
                  <option value="term">Term Life</option>
                  <option value="term100">Term-100</option>
                  <option value="whole">Whole Life</option>
                  <option value="universal">Universal Life</option>
                  <option value="critical_illness">Critical Illness</option>
                  <option value="disability">Disability</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Face Amount</label>
                <input type="number" min="0" value={form.face_amount} onChange={(e) => setForm({ ...form, face_amount: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-2 border rounded" data-testid="ct-face" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Monthly Benefit (CI/DI)</label>
                <input type="number" min="0" value={form.monthly_benefit} onChange={(e) => setForm({ ...form, monthly_benefit: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Premium</label>
                <input type="number" min="0" step="0.01" value={form.premium} onChange={(e) => setForm({ ...form, premium: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-2 border rounded" data-testid="ct-premium" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Frequency</label>
                <select value={form.payment_frequency} onChange={(e) => setForm({ ...form, payment_frequency: e.target.value })} className="w-full px-2 py-2 border rounded">
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annual">Semi-annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Term / Duration</label>
                <input value={form.term_or_duration} onChange={(e) => setForm({ ...form, term_or_duration: e.target.value })} className="w-full px-2 py-2 border rounded" placeholder="e.g. 20 years" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Policy #</label>
                <input value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} className="w-full px-2 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Application Date</label>
                <input type="date" value={form.application_date} onChange={(e) => setForm({ ...form, application_date: e.target.value })} className="w-full px-2 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Issue Date</label>
                <input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="w-full px-2 py-2 border rounded" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium mb-1">Riders (comma-separated)</label>
                <input value={form.riders} onChange={(e) => setForm({ ...form, riders: e.target.value })} className="w-full px-2 py-2 border rounded" placeholder="e.g. Waiver of Premium, CI Rider" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-2 py-2 border rounded">
                  <option value="submitted">Submitted</option>
                  <option value="issued">Issued</option>
                  <option value="declined">Declined</option>
                  <option value="withdrawn">Withdrawn</option>
                </select>
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs font-medium mb-1">Beneficiary</label>
                <input value={form.beneficiary} onChange={(e) => setForm({ ...form, beneficiary: e.target.value })} className="w-full px-2 py-2 border rounded" />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs font-medium mb-1">Notes</label>
                <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-2 py-2 border rounded" />
              </div>
              <div className="md:col-span-3">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium">Rationale — why this coverage was taken</label>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={aiExpand} disabled={aiLoading}
                    className="h-7 text-xs border-[#0077B6] text-[#0077B6] hover:bg-blue-50"
                    title={editingId ? 'Use AI to expand your rationale' : 'Save the coverage first to enable AI'}
                    data-testid="ct-ai-rationale-btn"
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1" />{aiLoading ? 'Expanding…' : 'AI Expand'}
                  </Button>
                </div>
                <textarea rows="3" value={form.rationale} onChange={(e) => setForm({ ...form, rationale: e.target.value })} className="w-full px-2 py-2 border rounded" placeholder="Brief note on why the client chose this coverage — click AI Expand to turn it into a compliance paragraph." data-testid="ct-rationale" />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => { setShowAdd(false); setEditingId(null); setForm(blankRow()); }}>
                <X className="h-4 w-4 mr-1" />Cancel
              </Button>
              <Button onClick={save} disabled={loading || !form.carrier} className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]" data-testid="save-coverage-taken-btn">
                <Save className="h-4 w-4 mr-1" />{loading ? 'Saving…' : (editingId ? 'Update' : 'Save Coverage')}
              </Button>
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <p className="text-gray-500 text-center py-6">No coverage recorded yet. Click "Add Coverage" once the client accepts a quote.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="coverage-taken-table">
              <thead>
                <tr className="bg-[#003D5C] text-white">
                  <th className="px-3 py-2 text-left">Insured</th>
                  <th className="px-3 py-2 text-left">Carrier</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">Face / Benefit</th>
                  <th className="px-3 py-2 text-right">Premium</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-b hover:bg-gray-50" data-testid={`coverage-row-${it.id}`}>
                    <td className="px-3 py-2 capitalize">{it.insured}</td>
                    <td className="px-3 py-2">{it.carrier}</td>
                    <td className="px-3 py-2">{it.product_name || it.product_type}</td>
                    <td className="px-3 py-2 text-right">
                      {fmt(it.face_amount)}{it.monthly_benefit ? ` + ${fmt(it.monthly_benefit)}/mo` : ''}
                    </td>
                    <td className="px-3 py-2 text-right">{fmt(it.premium)} / {it.payment_frequency}</td>
                    <td className="px-3 py-2 capitalize">{it.status}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(it)} title="Edit" data-testid={`edit-coverage-${it.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(it.id)} title="Delete" data-testid={`delete-coverage-${it.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===========================================================================
// Existing Policies Sub-Table (rendered on Fact Findings — Iter 31)
// ===========================================================================

const blankPolicy = () => ({
  insured: 'client',
  carrier: '',
  policy_number: '',
  product_type: 'term',
  face_amount: 0,
  premium: 0,
  payment_frequency: 'monthly',
  issue_date: '',
  term_remaining: '',
  riders: '',
  notes: '',
});

export const ExistingPoliciesSubTable = ({ policies, hasSpouse, onChange }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [editIdx, setEditIdx] = useState(-1);
  const [row, setRow] = useState(blankPolicy());

  const save = () => {
    if (!row.carrier && !row.policy_number) {
      alert('Enter at least the carrier OR policy number.');
      return;
    }
    const next = [...(policies || [])];
    if (editIdx >= 0) next[editIdx] = row;
    else next.push(row);
    onChange(next);
    setShowAdd(false);
    setEditIdx(-1);
    setRow(blankPolicy());
  };

  const remove = (idx) => {
    const next = [...(policies || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  const startEdit = (idx) => {
    setRow({ ...blankPolicy(), ...policies[idx] });
    setEditIdx(idx);
    setShowAdd(true);
  };

  return (
    <div data-testid="existing-policies-subtable">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold pb-2 border-b flex-1">Existing Policies (optional detail)</h3>
        <Button size="sm" variant="outline" onClick={() => { setShowAdd(!showAdd); setEditIdx(-1); setRow(blankPolicy()); }} data-testid="add-existing-policy-btn">
          <Plus className="h-4 w-4 mr-1" />{showAdd ? 'Cancel' : 'Add Policy'}
        </Button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Capture per-policy details for any existing coverage already in force. Optional — used for replacement comparison.
      </p>

      {showAdd && (
        <div className="border rounded-lg p-3 bg-blue-50 grid md:grid-cols-3 gap-2 mb-3">
          <div>
            <label className="block text-xs font-medium mb-1">Insured</label>
            <select value={row.insured} onChange={(e) => setRow({ ...row, insured: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm">
              <option value="client">Client</option>
              {hasSpouse && <option value="spouse">Spouse</option>}
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Carrier</label>
            <input value={row.carrier} onChange={(e) => setRow({ ...row, carrier: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Policy #</label>
            <input value={row.policy_number} onChange={(e) => setRow({ ...row, policy_number: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Type</label>
            <select value={row.product_type} onChange={(e) => setRow({ ...row, product_type: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm">
              <option value="term">Term Life</option>
              <option value="whole">Whole Life</option>
              <option value="universal">Universal Life</option>
              <option value="critical_illness">Critical Illness</option>
              <option value="disability">Disability</option>
              <option value="creditor">Creditor / Mortgage</option>
              <option value="group">Group</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Face Amount</label>
            <input type="number" min="0" value={row.face_amount} onChange={(e) => setRow({ ...row, face_amount: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-1.5 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Premium</label>
            <input type="number" min="0" step="0.01" value={row.premium} onChange={(e) => setRow({ ...row, premium: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-1.5 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Frequency</label>
            <select value={row.payment_frequency} onChange={(e) => setRow({ ...row, payment_frequency: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm">
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="semi_annual">Semi-annual</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Issue Date</label>
            <input type="date" value={row.issue_date} onChange={(e) => setRow({ ...row, issue_date: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Term Remaining</label>
            <input value={row.term_remaining} onChange={(e) => setRow({ ...row, term_remaining: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="e.g. 5 years" />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-medium mb-1">Notes / Riders</label>
            <input value={row.notes} onChange={(e) => setRow({ ...row, notes: e.target.value })} className="w-full px-2 py-1.5 border rounded text-sm" />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button size="sm" onClick={save} className="bg-[#0077B6]" data-testid="save-existing-policy-btn">
              <Save className="h-4 w-4 mr-1" />{editIdx >= 0 ? 'Update' : 'Add Policy'}
            </Button>
          </div>
        </div>
      )}

      {(policies || []).length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-1.5 text-left">Insured</th>
                <th className="px-2 py-1.5 text-left">Carrier</th>
                <th className="px-2 py-1.5 text-left">Policy #</th>
                <th className="px-2 py-1.5 text-left">Type</th>
                <th className="px-2 py-1.5 text-right">Face</th>
                <th className="px-2 py-1.5 text-right">Premium</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {policies.map((p, i) => (
                <tr key={`ep-${i}`} className="border-t">
                  <td className="px-2 py-1.5 capitalize">{p.insured}</td>
                  <td className="px-2 py-1.5">{p.carrier || '—'}</td>
                  <td className="px-2 py-1.5">{p.policy_number || '—'}</td>
                  <td className="px-2 py-1.5">{p.product_type || '—'}</td>
                  <td className="px-2 py-1.5 text-right">${(parseFloat(p.face_amount) || 0).toLocaleString()}</td>
                  <td className="px-2 py-1.5 text-right">${(parseFloat(p.premium) || 0).toLocaleString()}/{p.payment_frequency || 'mo'}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(i)} data-testid={`edit-existing-policy-${i}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(i)}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
